import React, { useState, useEffect, type ComponentType } from 'react';
import * as runtime from 'react/jsx-runtime';
import { evaluate } from '@mdx-js/mdx';
import remarkGfm from 'remark-gfm';

// Custom MDX components for preview
const MDXImage: React.FC<{
  src: string;
  alt?: string;
  caption?: string;
}> = ({ src, alt, caption }) => (
  <figure className="my-4">
    <img src={src} alt={alt || ''} className="rounded-lg max-w-full" />
    {caption && (
      <figcaption className="text-center text-sm text-muted-foreground mt-2">
        {caption}
      </figcaption>
    )}
  </figure>
);

const MDXVideo: React.FC<{
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
}> = ({ src, autoPlay = true, muted = true, loop = true, controls = true }) => (
  <video
    src={src}
    autoPlay={autoPlay}
    muted={muted}
    loop={loop}
    controls={controls}
    className="w-full rounded-lg my-4"
  />
);

const SkillCard: React.FC<{ name: string }> = ({ name }) => (
  <div className="border rounded-lg p-4 my-4 bg-muted/30 hover:bg-muted/50 transition-colors">
    <div className="flex items-center gap-2">
      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded">
        Skill
      </span>
      <span className="font-medium">{name}</span>
    </div>
    <p className="text-sm text-muted-foreground mt-2">
      Deep link: cueboard://skills/{name}
    </p>
  </div>
);

function extractYouTubeId(idOrUrl: string): string {
  if (idOrUrl.includes('youtube.com') || idOrUrl.includes('youtu.be')) {
    try {
      const url = new URL(idOrUrl);
      if (url.hostname.includes('youtube.com')) {
        return url.searchParams.get('v') || idOrUrl;
      }
      if (url.hostname.includes('youtu.be')) {
        return url.pathname.slice(1);
      }
    } catch {
      // Not a valid URL, treat as ID
    }
  }
  return idOrUrl;
}

const YouTube: React.FC<{ id: string; title?: string; start?: number }> = ({
  id,
  title,
  start,
}) => {
  const videoId = extractYouTubeId(id);
  const embedUrl = start
    ? `https://www.youtube.com/embed/${videoId}?start=${start}`
    : `https://www.youtube.com/embed/${videoId}`;

  return (
    <div className="my-4">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
        <iframe
          src={embedUrl}
          title={title || 'YouTube Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
      {title && (
        <p className="text-sm text-muted-foreground text-center mt-2">
          {title}
        </p>
      )}
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const components: Record<string, ComponentType<any>> = {
  Image: MDXImage,
  Video: MDXVideo,
  YouTube,
  SkillCard,
};

export interface MDXError {
  message: string;
  line?: number;
  column?: number;
}

interface MDXPreviewProps {
  source: string;
  onError?: (error: MDXError | null) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MDXContentType = ComponentType<{ components?: Record<string, ComponentType<any>> }>;

export function MDXPreview({ source, onError }: MDXPreviewProps) {
  const [Content, setContent] = useState<MDXContentType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [compiling, setCompiling] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function compile() {
      if (!source.trim()) {
        setContent(null);
        setError(null);
        setCompiling(false);
        return;
      }

      setCompiling(true);
      try {
        const { default: MDXContent } = await evaluate(source, {
          ...runtime,
          development: false,
          remarkPlugins: [remarkGfm],
        });
        if (!cancelled) {
          setContent(() => MDXContent as MDXContentType);
          setError(null);
          onError?.(null);
        }
      } catch (err) {
        if (!cancelled) {
          let errorMessage = 'MDX compilation error';
          let errorLine: number | undefined;
          let errorColumn: number | undefined;

          // MDX errors are VFileMessage objects with line/column properties
          const mdxError = err as {
            message?: string;
            reason?: string;
            line?: number;
            column?: number;
            position?: { start?: { line?: number; column?: number } };
          };

          errorLine = mdxError.line ?? mdxError.position?.start?.line;
          errorColumn = mdxError.column ?? mdxError.position?.start?.column;
          const message = mdxError.reason ?? mdxError.message ?? 'Unknown error';

          if (errorLine !== undefined) {
            errorMessage = `Line ${errorLine}${errorColumn !== undefined ? `, Column ${errorColumn}` : ''}: ${message}`;
          } else {
            errorMessage = message;
          }

          setError(errorMessage);
          setContent(null);
          onError?.({ message: errorMessage, line: errorLine, column: errorColumn });
        }
      } finally {
        if (!cancelled) {
          setCompiling(false);
        }
      }
    }

    // Debounce compilation
    const timer = setTimeout(compile, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source]);

  if (compiling) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full mr-2" />
        Compiling MDX...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive p-4 bg-destructive/10 rounded-lg border border-destructive/20">
        <p className="font-medium mb-2">MDX Compilation Error</p>
        <pre className="text-xs overflow-auto whitespace-pre-wrap">{error}</pre>
      </div>
    );
  }

  if (!Content) {
    return (
      <div className="text-muted-foreground text-center py-8">
        No content to preview. Start typing in the editor.
      </div>
    );
  }

  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <Content components={components} />
    </div>
  );
}

